import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import useScrollReveal from '../../hooks/useScrollReveal';
import SectionHeader from '../ui/SectionHeader';

type BadgeVariant = 'live' | 'beta' | 'coming';

interface Module {
  id: string;
  label: string;
  badge: BadgeVariant;
  title: string;
  desc: string;
  link: string;
  features: string[];
}

const modules: Module[] = [
  {
    id: 'dcr',
    label: 'ESG Data Collection',
    badge: 'live',
    title: 'ESG Data Collection & Reporting',
    desc: 'Structured, framework-mapped data collection for BRSR, CSRD, ESRS, and EU Taxonomy — with double materiality assessment, IRO identification, and multi-entity consolidation built in.',
    link: '/platform#esg-data',
    features: [
      'BRSR (India) — 9 principles, essential & leadership indicators, full data trail for annual report',
      'CSRD / ESRS — integrated double materiality, IRO mapping, NACE-linked Taxonomy alignment',
      'EU Taxonomy — KPI calculation (revenue, CapEx, OpEx) in the required disclosure format',
      'Multi-entity consolidation with role-based data ownership and approval workflows',
      'Audit trail: every submission, revision, and sign-off logged with identity and timestamp',
    ],
  },
  {
    id: 'cbam',
    label: 'CBAM',
    badge: 'live',
    title: 'CBAM Compliance',
    desc: 'Structured supplier carbon data collection, default value management, quarterly declaration support, and certificate cost modelling — for EU importers of covered goods.',
    link: '/platform#cbam',
    features: [
      'Supplier data requests for embedded direct and indirect emissions per tonne of product',
      'EU default values applied automatically where actual supplier data is unavailable',
      'Quarterly declaration output structured for the EU CBAM Registry',
      'Certificate cost modelling based on embedded carbon volumes and current EU ETS price',
      'Scope 3 Category 1 integration — CBAM data feeds your corporate carbon footprint automatically',
    ],
  },
  {
    id: 'pcf',
    label: 'PCF',
    badge: 'live',
    title: 'Product Carbon Footprint',
    desc: 'ISO 14067-aligned product-level carbon footprint calculation — covering Scope 1, 2 and 3 per product — with supplier data integration and customer-ready disclosure documents.',
    link: '/platform#pcf',
    features: [
      'ISO 14067 methodology — system boundary, functional unit, reproducible calculation',
      'Scope 1, 2 and 3 emissions per product unit including upstream supplier data',
      'Emission factor library for secondary data where supplier actuals are unavailable',
      'Customer-ready PCF disclosure documents for tender responses and ESG reports',
      'Data feeds CBAM declarations and CCF Scope 3 — calculated once, used across all three',
    ],
  },
  {
    id: 'ccf',
    label: 'CCF',
    badge: 'live',
    title: 'Corporate Carbon Footprint',
    desc: 'GHG Protocol-aligned Scope 1, 2 and 3 accounting — with ESRS E1 disclosure outputs, science-based target tracking, and multi-entity consolidation.',
    link: '/platform#ccf',
    features: [
      'GHG Protocol Corporate Standard — Scope 1, 2 (market-based & location-based), all 15 Scope 3 categories',
      'ESRS E1 disclosure outputs: gross emissions, disaggregation by source, intensity metrics',
      'Science-based targets and reduction tracking — year-on-year comparisons, restated baselines',
      'Multi-entity consolidation with documented boundary decisions (operational / financial / equity)',
      'Scope 3 Category 1 benefits from CBAM and supplier PCF data — collected once, used everywhere',
    ],
  },
  {
    id: 'proc',
    label: 'Procurement',
    badge: 'live',
    title: 'Sustainable Procurement',
    desc: 'Supplier ESG assessments, Scope 3 supply chain data collection, SAP Ariba integration, and supplier portal — bringing sustainability into the sourcing workflow.',
    link: '/platform#procurement',
    features: [
      'Structured supplier ESG questionnaires — configured by spend category, risk profile, and framework',
      'CSRD Scope 3 Category 1 & 2 data collection from key suppliers via dedicated portal',
      'SAP Ariba integration — sustainability scores visible in sourcing events, linked to procurement decisions',
      'Risk tiering by spend, geography, commodity, or framework-specific criteria',
      'CBAM data collected via the same portal — eliminating three separate requests for overlapping data',
    ],
  },
];

export default function ModuleTabs(): React.ReactElement {
  const [active, setActive] = useState('dcr');
  const ref = useScrollReveal();

  return (
    <section
      className="modules section-pad"
      id="modules"
      ref={ref as React.RefObject<HTMLElement>}
    >
      <div className="container">
        <SectionHeader
          eyebrow="Five integrated modules"
          title="Every ESG obligation. One platform."
          description="Actula covers the full spectrum — from framework configuration and data collection through carbon calculation, Taxonomy alignment, and audit-ready export."
        />

        <div className="modules-tabs reveal" role="tablist">
          {modules.map(m => (
            <button
              key={m.id}
              className={`tab-btn${active === m.id ? ' tab-btn--active' : ''}`}
              role="tab"
              aria-selected={active === m.id}
              aria-controls={`panel-${m.id}`}
              onClick={() => setActive(m.id)}
            >
              {m.label}
            </button>
          ))}
        </div>

        {modules.map(m => (
          <div
            key={m.id}
            id={`panel-${m.id}`}
            role="tabpanel"
            className={`tab-panel${active === m.id ? ' tab-panel--active' : ''}`}
          >
            <div className="module-card reveal">
              <div className="module-card-left">
                <span className={`badge badge-${m.badge}`}>
                  {m.badge.charAt(0).toUpperCase() + m.badge.slice(1)}
                </span>
                <p className="module-card-title">{m.title}</p>
                <p className="module-card-desc">{m.desc}</p>
                <Link to={m.link} className="link-arrow">Full module details</Link>
              </div>
              <div className="module-card-right">
                <ul className="module-feature-list">
                  {m.features.map((f, i) => (
                    <li key={i} className="module-feature">
                      <span className="module-check" aria-hidden="true">&#10003;</span>
                      <span className="module-feature-text">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
